/**
 * Material configuration for 3D models
 * Each material defines texture paths and Three.js material properties
 *
 * KTX2 Support:
 * - The TextureManager will automatically look for .ktx2 versions of textures
 * - If KTX2 files exist, they will be used for faster loading
 * - Falls back to PNG/JPG if KTX2 files are not available
 * - To convert your textures to KTX2, see /docs/KTX2_CONVERSION_GUIDE.md
 */

export const materialConfig = {
  'oak-wood': {
    name: 'Oak Wood',
    label: 'Eikenhout',
    type: 'directional', // Wood grain has direction
    basePath: '/data/oak-wood-bare-unity',
    textures: {
      albedo: 'oak-wood-bare_albedo.png',
      normal: 'oak-wood-bare_normal-ogl.png',
      ao: 'oak-wood-bare_ao.png',
      height: 'oak-wood-bare_height.png',
      // metallic is .psd file, skip for now
    },
    properties: {
      roughness: 0.7,
      metalness: 0.0,
      normalScale: [1.2, 1.2],
      aoMapIntensity: 0.8,
      displacementScale: 0.005,
      envMapIntensity: 0.5,
    },
    textureScale: 1, // Initial scale (TextureManager will calculate optimal value)
    unitsPerRepeat: 2000, // Grain pattern repeats every 2000mm (2m) - larger grain for structural wood
  },

  'streaky-plywood': {
    name: 'Streaky Plywood',
    label: 'Gestreept Multiplex',
    type: 'directional', // Plywood grain has direction
    basePath: '/data/streaky-plywood-unity',
    textures: {
      albedo: 'streaky-plywood_albedo.png',
      normal: 'streaky-plywood_normal-ogl.png',
      ao: 'streaky-plywood_ao.png',
      height: 'streaky-plywood_height.png',
    },
    properties: {
      roughness: 0.6,
      metalness: 0.0,
      normalScale: [1.2, 1.2],
      aoMapIntensity: 0.9,
      displacementScale: 0.005,
      envMapIntensity: 0.5,
    },
    textureScale: 1, // Initial scale (TextureManager will calculate optimal value)
    unitsPerRepeat: 2000, // Grain repeats every 2000mm (2m)
  },

  'grainy-concrete': {
    name: 'Grainy Concrete',
    label: 'Korrelig Beton',
    type: 'non-directional', // Concrete has no grain direction
    basePath: '/data/grainy-concrete-unity',
    textures: {
      albedo: 'grainy-concrete_albedo.png',
      normal: 'grainy-concrete_normal-ogl.png',
      ao: 'grainy-concrete_ao.png',
      height: 'grainy-concrete_height.png',
    },
    properties: {
      roughness: 0.85,
      metalness: 0.0,
      normalScale: [0.8, 0.8],
      aoMapIntensity: 1.0,
      displacementScale: 0.003,
      envMapIntensity: 0.3,
    },
    textureScale: 3, // Concrete needs less repetition than wood
    unitsPerRepeat: 2.25, // Concrete pattern repeats every 125mm (test value based on UV debug)
  },

  'grass': {
    name: 'Grass',
    label: 'Gras',
    type: 'non-directional', // Grass has no specific direction
    basePath: '/data/Grass001_4K-JPG',
    textures: {
      albedo: 'Grass001_4K-JPG_Color.jpg',
      normal: 'Grass001_4K-JPG_NormalGL.jpg',
      ao: 'Grass001_4K-JPG_AmbientOcclusion.jpg',
      height: 'Grass001_4K-JPG_Displacement.jpg',
      roughness: 'Grass001_4K-JPG_Roughness.jpg',
    },
    properties: {
      roughness: 0.8,
      metalness: 0.0,
      normalScale: [1.5, 1.5],
      aoMapIntensity: 0.7,
      displacementScale: 0.01,
      envMapIntensity: 0.4,
    },
    textureScale: 100, // Grass needs high repetition for detailed look
    unitsPerRepeat: 2000, // Grass pattern repeats every 200mm (for TextureManager)
  },

  'bare-wood1': {
    name: 'Bare Wood 1',
    label: 'Kaal Hout 1',
    type: 'directional', // Wood grain has direction
    basePath: '/data/bare-wood1-unity',
    textures: {
      albedo: 'bare-wood1_albedo.png',
      normal: 'bare-wood1_normal-ogl.png',
      ao: 'bare-wood1_ao.png',
      height: 'bare-wood1_height.png',
    },
    properties: {
      roughness: 0.7,
      metalness: 0.0,
      normalScale: [1.2, 1.2],
      aoMapIntensity: 0.8,
      displacementScale: 0.005,
      envMapIntensity: 0.5,
    },
    textureScale: 1, // Initial scale (TextureManager will calculate optimal value)
    unitsPerRepeat: 2000, // Grain pattern repeats every 2000mm (2m)
  },

  'subtle-grained-wood': {
    name: 'Subtle Grained Wood',
    label: 'Subtiel Geaderd Hout',
    type: 'directional', // Wood grain has direction
    basePath: '/data/subtle-grained-wood-unity',
    textures: {
      albedo: 'subtle-grained-wood_albedo.png',
      normal: 'subtle-grained-wood_normal-ogl.png',
      ao: 'subtle-grained-wood_ao.png',
      height: 'subtle-grained-wood_height.png',
    },
    properties: {
      roughness: 0.65,
      metalness: 0.0,
      normalScale: [1.0, 1.0],
      aoMapIntensity: 0.75,
      displacementScale: 0.004,
      envMapIntensity: 0.5,
    },
    textureScale: 1, // Initial scale (TextureManager will calculate optimal value)
    unitsPerRepeat: 2000, // Grain pattern repeats every 2000mm (2m)
  },

  'wispy-grass-meadow': {
    name: 'Wispy Grass Meadow',
    label: 'Golvend Grasland',
    type: 'non-directional', // Grass has no specific direction
    basePath: '/data/whispy-grass-meadow-unity',
    textures: {
      albedo: 'wispy-grass-meadow_albedo.png',
      normal: 'wispy-grass-meadow_normal-ogl.png',
      ao: 'wispy-grass-meadow_ao.png',
      height: 'wispy-grass-meadow_height.png',
    },
    properties: {
      roughness: 0.8,
      metalness: 0.0,
      normalScale: [1.5, 1.5],
      aoMapIntensity: 0.7,
      displacementScale: 0.01,
      envMapIntensity: 0.4,
    },
    textureScale: 10, // Grass needs high repetition for detailed look
    unitsPerRepeat: 2800, // Grass pattern repeats every 200mm (for TextureManager)
  },

  'cobblestone-curved': {
    name: 'Cobblestone Curved',
    label: 'Gebogen Kasseie',
    type: 'non-directional', // Stone has no specific direction
    basePath: '/data/cobblestone-curved_2_unity',
    textures: {
      albedo: 'cobblestone-curved_2_albedo.png',
      normal: 'cobblestone-curved_2_normal-ogl.png',
      ao: 'cobblestone-curved_2_ao.png',
      height: 'cobblestone-curved_2_height.png',
    },
    properties: {
      roughness: 0.9,
      metalness: 0.0,
      normalScale: [1.0, 1.0],
      aoMapIntensity: 1.0,
      displacementScale: 0.008,
      envMapIntensity: 0.3,
    },
    textureScale: 5, // Stone needs moderate repetition
    unitsPerRepeat: 2.1, // Stone pattern repeats every 500mm (for TextureManager)
  },

  'wood-siding': {
    name: 'Wood Siding',
    label: 'Houten Gevelbekleding',
    type: 'directional',
    orientation: 'vertical',
    basePath: '/data/WoodSiding008_1K-JPG',
    textures: {
      albedo: 'WoodSiding008_1K-JPG_Color.jpg',
      normal: 'WoodSiding008_1K-JPG_NormalGL.jpg',
      ao: 'WoodSiding008_1K-JPG_AmbientOcclusion.jpg',
      height: 'WoodSiding008_1K-JPG_Displacement.jpg',
      roughness: 'WoodSiding008_1K-JPG_Roughness.jpg',
    },
    properties: {
      roughness: 0.85,
      metalness: 0.0,
      normalScale: [1.2, 1.2],
      aoMapIntensity: 0.8,
      displacementScale: 0.005,
      envMapIntensity: 0.2,
    },
    textureScale: 1,
    unitsPerRepeat: 1,
  },

  'wood-siding-5': {
    name: 'Wood Siding Light',
    label: 'Gevelbekleding Licht',
    type: 'directional',
    orientation: 'vertical',
    basePath: '/data/WoodSiding005_1K-JPG',
    textures: {
      albedo: 'WoodSiding005_1K-JPG_Color.jpg',
      normal: 'WoodSiding005_1K-JPG_NormalGL.jpg',
      ao: 'WoodSiding005_1K-JPG_AmbientOcclusion.jpg',
      height: 'WoodSiding005_1K-JPG_Displacement.jpg',
      roughness: 'WoodSiding005_1K-JPG_Roughness.jpg',
    },
    properties: {
      roughness: 0.85,
      metalness: 0.0,
      normalScale: [1.2, 1.2],
      aoMapIntensity: 0.8,
      displacementScale: 0.005,
      envMapIntensity: 0.2,
    },
    textureScale: 1,
    unitsPerRepeat: 1.5,
  },

  'wood-siding-9': {
    name: 'Wood Siding Natural',
    label: 'Gevelbekleding Naturel',
    type: 'directional',
    orientation: 'horizontal',
    basePath: '/data/WoodSiding009_1K-JPG',
    textures: {
      albedo: 'WoodSiding009_1K-JPG_Color.jpg',
      normal: 'WoodSiding009_1K-JPG_NormalGL.jpg',
      ao: 'WoodSiding009_1K-JPG_AmbientOcclusion.jpg',
      height: 'WoodSiding009_1K-JPG_Displacement.jpg',
      roughness: 'WoodSiding009_1K-JPG_Roughness.jpg',
    },
    properties: {
      roughness: 0.85,
      metalness: 0.0,
      normalScale: [1.2, 1.2],
      aoMapIntensity: 0.8,
      displacementScale: 0.005,
      envMapIntensity: 0.2,
    },
    textureScale: 1,
    unitsPerRepeat: 1,
  },

  'wood-siding-13': {
    name: 'Wood Siding Dark',
    label: 'Gevelbekleding Donker',
    type: 'directional',
    orientation: 'horizontal',
    basePath: '/data/WoodSiding013_1K-JPG',
    textures: {
      albedo: 'WoodSiding013_1K-JPG_Color.jpg',
      normal: 'WoodSiding013_1K-JPG_NormalGL.jpg',
      ao: 'WoodSiding013_1K-JPG_AmbientOcclusion.jpg',
      height: 'WoodSiding013_1K-JPG_Displacement.jpg',
      roughness: 'WoodSiding013_1K-JPG_Roughness.jpg',
    },
    properties: {
      roughness: 0.85,
      metalness: 0.0,
      normalScale: [1.2, 1.2],
      aoMapIntensity: 0.8,
      displacementScale: 0.005,
      envMapIntensity: 0.2,
    },
    textureScale: 1,
    unitsPerRepeat: 1,
  },

  'wood-planks': {
    name: 'Wood Planks',
    label: 'Houten Planken',
    type: 'directional',
    orientation: 'horizontal',
    basePath: '/data/Planks020_1K-JPG',
    textures: {
      albedo: 'Planks020_1K-JPG_Color.jpg',
      normal: 'Planks020_1K-JPG_NormalGL.jpg',
      ao: 'Planks020_1K-JPG_AmbientOcclusion.jpg',
      height: 'Planks020_1K-JPG_Displacement.jpg',
      roughness: 'Planks020_1K-JPG_Roughness.jpg',
    },
    properties: {
      roughness: 0.85,
      metalness: 0.0,
      normalScale: [1.2, 1.2],
      aoMapIntensity: 0.8,
      displacementScale: 0.005,
      envMapIntensity: 0.2,
    },
    textureScale: 1,
    unitsPerRepeat: 1,
  },
};

/**
 * Get list of material options for use in form selectors
 * @param {Array<string>} materialKeys - Optional array of material keys to include. If not provided, returns all materials.
 */
export const getMaterialOptions = (materialKeys = null) => {
  const entries = Object.entries(materialConfig);

  // If materialKeys is provided, filter to only those materials
  const filteredEntries = materialKeys
    ? entries.filter(([key]) => materialKeys.includes(key))
    : entries;

  return filteredEntries.map(([key, config]) => ({
    value: key,
    label: config.label,
    name: config.name,
  }));
};
